import type { Moment } from 'moment';

import { translate } from '@wecasa/i18n';

import type { Activity, IncomeLevelRange, YoungestChildAge } from '../types/commons';
import { getDefaultSpotTimeRange } from './appointment';
import { formatDate, setTime } from './date';
import { formatPrice } from './formatting';
import moment from './moment';
import { parseTime } from './time';

const t = translate('basket.childcare');

export const renderAgeGroup = (youngestChildAge: string, childrenCount?: number) =>
  t(`.age_group.${childrenCount && childrenCount > 1 ? 'multiple' : 'single'}`, {
    ageGroup: t(`inputs.youngest_child_age.values.${youngestChildAge}`),
  });

export const renderActivities = (activities: Activity[]) => {
  const sorted = [
    // Always display "other" activity as last activity
    ...activities.filter((activity) => activity.kind !== 'other'),
    ...activities.filter((activity) => activity.kind === 'other'),
  ];

  return sorted
    .map((activity) => {
      if (activity.kind === 'other') {
        return t('.other_activity', { title: activity.title, comment: activity.comment });
      }

      return activity.title;
    })
    .join(' + ');
};

export const renderChildren = (childrenCount?: number) => (childrenCount ? t(`.children.${childrenCount}`) : '');

export const renderDateRange = (startDate?: Moment | null, endDate?: Moment | null) =>
  startDate && endDate ? t('.date_range', { start: formatDate(startDate), end: formatDate(endDate) }) : '';

export const getOtherActivityComment = (activities: Activity[]) => {
  const otherActivity = activities.find((a) => a.kind === 'other');
  return otherActivity ? otherActivity.comment : undefined;
};

export const allowanceIncomeLevelTitle = (level: IncomeLevelRange) => {
  const options = { precision: 0 };

  if (level.minimumIncome && level.maximumIncome) {
    return t('childcare.allowance.level.between', {
      minimum: formatPrice(level.minimumIncome, options),
      maximum: formatPrice(level.maximumIncome, options),
    });
  }
  if (level.minimumIncome) {
    return t('childcare.allowance.level.above', {
      minimum: formatPrice(level.minimumIncome, options),
    });
  }
  if (level.maximumIncome) {
    return t('childcare.allowance.level.below', {
      maximum: formatPrice(level.maximumIncome, options),
    });
  }
  return t('childcare.allowance.level.unknown');
};

export const allowanceReferenceYear = (currentDate?: Moment | null): number => {
  if (!currentDate) return allowanceReferenceYear(moment());

  const currentYear = currentDate.year();
  return currentYear === 2021 ? currentYear - 2 : currentYear - 1;
};

export const getEndDateForSpotChildcare = (date: Moment, endTime?: string, isNightBabysitting?: boolean) => {
  const baseDate = setTime(date, endTime);

  if (isNightBabysitting) {
    const nextDayEndTime = parseTime(getDefaultSpotTimeRange(isNightBabysitting).end);

    // TODO: Should we also check the minutes here if baseDate.hour() === 1 (e.g. 01:30)?
    if (baseDate.hour() >= 0 && baseDate.hour() <= nextDayEndTime.hour()) {
      return baseDate.add(1, 'day');
    }
  }

  return baseDate;
};

type IsEligibleForAllowanceParams = {
  youngestChildAge?: YoungestChildAge;
};

export const isEligibleForAllowance = ({ youngestChildAge }: IsEligibleForAllowanceParams) =>
  youngestChildAge !== 'more_than_6';
